import React from "react";
import { toast } from "sonner";
import LegalLayout, { LegalSection, LegalList, CONTACT_EMAIL, ENTITY_NAME } from "@/components/legal/LegalLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldOff, Mail } from "lucide-react";

const REQUEST_KEY = "gpp_do_not_sell_request_v1";

export default function DoNotSellPage() {
    const [email, setEmail] = React.useState("");
    const [notes, setNotes] = React.useState("");
    const [confirm, setConfirm] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);

    const submit = (e) => {
        e.preventDefault();
        const trimmed = email.trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
            toast.error("Please enter a valid email address.");
            return;
        }
        if (!confirm) {
            toast.error("Please confirm you are a California resident or an authorized agent.");
            return;
        }
        setSubmitting(true);
        try {
            // Persist locally so we have a record on this device and the banner won't reappear
            window.localStorage.setItem(
                REQUEST_KEY,
                JSON.stringify({ email: trimmed, notes, ts: new Date().toISOString() })
            );
            // Also fire a mailto so the request reaches us even without a live API
            const subject = encodeURIComponent("CCPA / CPRA — Do Not Sell or Share Request");
            const body = encodeURIComponent(
                `Email on file: ${trimmed}\nResidency: California (or authorized agent)\n\nNotes:\n${notes || "(none)"}\n`
            );
            window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
            toast.success("Request recorded. Your email client should now open a pre-filled message to our privacy team.");
        } catch (_e) {
            toast.error("We couldn't record your request locally. Please email us directly at " + CONTACT_EMAIL + ".");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <LegalLayout
            title="Do Not Sell or Share My Personal Information"
            subtitle="A right available to California residents under the California Consumer Privacy Act (CCPA), as amended by the California Privacy Rights Act (CPRA)."
            testId="do-not-sell-page"
        >
            <LegalSection id="summary" title="Summary">
                <p>
                    {ENTITY_NAME} does <strong>not</strong> sell personal information in exchange for money, and we do <strong>not</strong> share personal information for cross-context behavioral advertising as those terms are defined under the CCPA / CPRA.
                </p>
                <p>
                    California residents have the right to direct us to stop any such sale or sharing should our practices ever change. Use the form below to record your "Do Not Sell or Share" preference with us. You may also email us directly at{" "}
                    <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#0B4A34] underline">{CONTACT_EMAIL}</a>.
                </p>
            </LegalSection>

            <LegalSection id="form" title="Submit a request">
                <p className="text-sm">
                    We will honor your request within the time frames required by California law (generally 15 business days for opt-out requests). You do not need an account to submit this request.
                </p>

                <form onSubmit={submit} className="mt-4 rounded-2xl border border-[#E9D9C1] bg-white/70 p-5 sm:p-6 space-y-5" data-testid="do-not-sell-form">
                    <div>
                        <Label htmlFor="dns-email" className="text-[#062A1E] font-semibold text-sm">
                            Email address
                        </Label>
                        <div className="mt-1.5 relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#1B2621]/40" />
                            <Input
                                id="dns-email"
                                type="email"
                                inputMode="email"
                                placeholder="you@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="pl-9 h-11 bg-white border-[#E9D9C1] text-[#062A1E] placeholder:text-[#1B2621]/40 focus-visible:ring-[#0B4A34]"
                                required
                                data-testid="do-not-sell-email"
                            />
                        </div>
                        <p className="mt-1.5 text-xs text-[#1B2621]/60">
                            Use the email you joined the waitlist with, or any email associated with your account when the App launches.
                        </p>
                    </div>

                    <div>
                        <Label htmlFor="dns-notes" className="text-[#062A1E] font-semibold text-sm">
                            Additional notes (optional)
                        </Label>
                        <Textarea
                            id="dns-notes"
                            placeholder="Anything you'd like us to know about your request."
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            className="mt-1.5 min-h-[88px] bg-white border-[#E9D9C1] text-[#062A1E] placeholder:text-[#1B2621]/40 focus-visible:ring-[#0B4A34]"
                            data-testid="do-not-sell-notes"
                        />
                    </div>

                    <label className="flex items-start gap-3 cursor-pointer">
                        <Checkbox
                            id="dns-confirm"
                            checked={confirm}
                            onCheckedChange={(v) => setConfirm(Boolean(v))}
                            className="mt-0.5 border-[#0B4A34] data-[state=checked]:bg-[#0B4A34]"
                            data-testid="do-not-sell-confirm"
                        />
                        <span className="text-sm text-[#1B2621]/85 leading-snug">
                            I confirm that I am a California resident, or I am an authorized agent acting on behalf of a California resident, and the information above is accurate.
                        </span>
                    </label>

                    <div className="flex flex-wrap items-center gap-3 pt-1">
                        <Button
                            type="submit"
                            disabled={submitting}
                            className="h-11 px-5 rounded-xl bg-[#0B4A34] hover:bg-[#0F6244] text-[#FBF7EE] font-semibold shadow-[0_10px_24px_rgba(6,42,30,0.18)]"
                            data-testid="do-not-sell-submit"
                        >
                            <ShieldOff className="h-4 w-4 mr-1.5" />
                            {submitting ? "Recording…" : "Submit my request"}
                        </Button>
                        <a
                            href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("CCPA — Do Not Sell or Share Request")}`}
                            className="text-sm text-[#0B4A34] hover:text-[#137A55] underline underline-offset-2 font-semibold"
                            data-testid="do-not-sell-mailto"
                        >
                            Or email {CONTACT_EMAIL}
                        </a>
                    </div>
                </form>
            </LegalSection>

            <LegalSection id="what-this-means" title="What this request does">
                <LegalList
                    items={[
                        "Records your direction that we not sell or share your personal information, even if our practices change in the future.",
                        "Suppresses optional analytics cookies on this browser when you reject them in our cookie banner.",
                        "Does not remove your waitlist subscription. To unsubscribe, use the link in any email or contact us directly.",
                        "Does not delete data we hold about you. Submit a separate deletion request to " + CONTACT_EMAIL + " if you want your data deleted.",
                    ]}
                />
            </LegalSection>

            <LegalSection id="agents" title="Authorized agents">
                <p>
                    You may designate an authorized agent to submit a Do Not Sell or Share request on your behalf. The agent should include in the notes field above (i) the agent's name and contact information, (ii) signed written permission from the consumer, and (iii) any other information we may reasonably require to verify identity under California law.
                </p>
            </LegalSection>

            <LegalSection id="non-discrimination" title="Non-discrimination">
                <p>
                    We will not discriminate against you for exercising your right to opt out. Waitlist bonuses, launch invitations, and access to the App at release will remain available to you on the same terms as any other waitlist member.
                </p>
            </LegalSection>
        </LegalLayout>
    );
}
